import {
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
    type ImgHTMLAttributes,
    type SyntheticEvent,
} from 'react';

import { cn } from '@/lib/utils';
import { previewSrcSet } from '@/lib/media-url';

export type SoftImageProps = ImgHTMLAttributes<HTMLImageElement>;

/** Survives SPA remounts so back-nav to home does not fade already-seen covers. */
const revealedSrcs = new Set<string>();

function revealKey(src: unknown, srcSet?: string) {
    if (typeof src === 'string' && src) return src;
    return srcSet || '';
}

/**
 * Native `<img>` with a soft opacity fade-in after load.
 * Timing lives on `.fade-in-image` in `styles.css`. Handles cached images via
 * ref + layout effect (Safari may skip a late onLoad).
 */
export function SoftImage({
    className,
    onLoad,
    onError,
    alt = '',
    src,
    srcSet,
    sizes,
    ...rest
}: SoftImageProps) {
    const resolvedSrcSet = srcSet ?? previewSrcSet(typeof src === 'string' ? src : undefined);
    const key = revealKey(src, resolvedSrcSet);
    const [loaded, setLoaded] = useState(() => Boolean(key && revealedSrcs.has(key)));
    const imgRef = useRef<HTMLImageElement | null>(null);

    const markLoaded = useCallback(() => {
        if (key) revealedSrcs.add(key);
        setLoaded(true);
    }, [key]);

    // Sync load state when the URL changes (cached decode may skip onLoad).
    useLayoutEffect(() => {
        const node = imgRef.current;
        if (node?.complete && node.naturalWidth > 0) {
            markLoaded();
        } else if (key && revealedSrcs.has(key)) {
            setLoaded(true);
        } else {
            setLoaded(false);
        }
    }, [src, resolvedSrcSet, key, markLoaded]);

    const handleLoad = useCallback(
        (event: SyntheticEvent<HTMLImageElement>) => {
            markLoaded();
            onLoad?.(event);
        },
        [markLoaded, onLoad],
    );

    const handleError = useCallback(
        (event: SyntheticEvent<HTMLImageElement>) => {
            markLoaded();
            onError?.(event);
        },
        [markLoaded, onError],
    );

    const handleRef = useCallback((node: HTMLImageElement | null) => {
        imgRef.current = node;
    }, []);

    return (
        <img
            {...rest}
            src={src}
            srcSet={resolvedSrcSet}
            sizes={sizes}
            alt={alt}
            ref={handleRef}
            className={cn(
                'fade-in-image',
                loaded && 'fade-in-image--loaded',
                className,
            )}
            onLoad={handleLoad}
            onError={handleError}
        />
    );
}
